import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ReportsWindow } from '@mmf/contracts';
import { fetchReportsSummary } from '../api/client';

const WINDOW_LABELS: Record<ReportsWindow, string> = {
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  all: 'All time',
};

/**
 * Administrator-only. Every number here is computed on demand from the
 * existing tables (see ReportingRepository) — no new data store, no
 * scheduled rollup, so this is always current as of the moment it's opened.
 */
export function ReportsPage(): React.JSX.Element {
  const [window, setWindow] = useState<ReportsWindow>('30');

  const summary = useQuery({
    queryKey: ['admin', 'reports', window],
    queryFn: () => fetchReportsSummary(window),
  });

  return (
    <section aria-labelledby="reports-heading" data-testid="reports-page">
      <div className="topic-list-header">
        <h3 id="reports-heading">Reports</h3>
        <span>
          <label htmlFor="reports-window">Window</label>
          <select
            id="reports-window"
            value={window}
            onChange={(event) => setWindow(event.target.value as ReportsWindow)}
          >
            {(Object.keys(WINDOW_LABELS) as ReportsWindow[]).map((value) => (
              <option key={value} value={value}>
                {WINDOW_LABELS[value]}
              </option>
            ))}
          </select>
        </span>
      </div>

      {summary.isLoading && <p role="status">Loading reports…</p>}

      {summary.error && (
        <p role="alert" data-testid="reports-error">
          Could not load reports.
        </p>
      )}

      {summary.data && (
        <>
          <ul className="report-totals" data-testid="report-totals">
            <li>
              <strong>{summary.data.totals.topicCount}</strong>
              <span className="hint">Topics</span>
            </li>
            <li>
              <strong>{summary.data.totals.commentCount}</strong>
              <span className="hint">Comments</span>
            </li>
            <li>
              <strong>{summary.data.totals.voteCount}</strong>
              <span className="hint">Votes cast</span>
            </li>
            <li>
              <strong>{summary.data.totals.activeMemberCount}</strong>
              <span className="hint">Active members</span>
            </li>
          </ul>

          <h4>Discussions by category</h4>
          <div className="report-table-wrapper">
            <table className="report-table" data-testid="report-category-activity">
              <thead>
                <tr>
                  <th scope="col">Main category</th>
                  <th scope="col">Category</th>
                  <th scope="col">Topics</th>
                  <th scope="col">Comments</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.categoryActivity.map((row) => (
                  <tr key={row.categoryId}>
                    <td>{row.mainCategoryName}</td>
                    <td>{row.categoryName}</td>
                    <td>{row.topicCount}</td>
                    <td>{row.commentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4>Top contributors</h4>
          {summary.data.topContributors.length === 0 ? (
            <p className="hint">No activity in this window.</p>
          ) : (
            <div className="report-table-wrapper">
              <table className="report-table" data-testid="report-top-contributors">
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Topics</th>
                    <th scope="col">Comments</th>
                    <th scope="col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.topContributors.map((row) => (
                    <tr key={row.userId}>
                      <td>{row.displayName}</td>
                      <td>{row.topicCount}</td>
                      <td>{row.commentCount}</td>
                      <td>{row.totalCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>Moderation throughput</h4>
          <ul className="report-totals" data-testid="report-moderation-throughput">
            <li>
              <strong>{summary.data.moderationThroughput.autoFlagged}</strong>
              <span className="hint">Auto-flagged</span>
            </li>
            <li>
              <strong>{summary.data.moderationThroughput.autoBlocked}</strong>
              <span className="hint">Auto-blocked</span>
            </li>
            <li>
              <strong>{summary.data.moderationThroughput.userReported}</strong>
              <span className="hint">Reported by members</span>
            </li>
            <li>
              <strong>{summary.data.moderationThroughput.approved}</strong>
              <span className="hint">Approved</span>
            </li>
            <li>
              <strong>{summary.data.moderationThroughput.rejected}</strong>
              <span className="hint">Rejected</span>
            </li>
          </ul>

          <h4>Bug &amp; enhancement trends, by week</h4>
          {summary.data.weeklyTrends.length === 0 ? (
            <p className="hint">No Bug or Enhancement topics in this window.</p>
          ) : (
            <div className="report-table-wrapper">
              <table className="report-table" data-testid="report-weekly-trends">
                <thead>
                  <tr>
                    <th scope="col">Week of</th>
                    <th scope="col">Category</th>
                    <th scope="col">Topics</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.data.weeklyTrends.map((row) => (
                    <tr key={`${row.weekStart.toISOString()}-${row.categoryKey}`}>
                      <td>{row.weekStart.toLocaleDateString()}</td>
                      <td>{row.categoryKey}</td>
                      <td>{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
